import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  category: string;
  link?: string;
  actorName?: string;
  read: boolean;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {

  private readonly POLL_MS = 30000;

  private _items$ = new BehaviorSubject<AppNotification[]>([]);
  private _unread$ = new BehaviorSubject<number>(0);

  items$ = this._items$.asObservable();
  unread$ = this._unread$.asObservable();

  private poll?: Subscription;

  constructor(private api: ApiService, private auth: AuthService) {}

  start(): void {
    if (this.poll || !this.auth.getCurrentUser()) return;
    this.poll = timer(0, this.POLL_MS).pipe(
      switchMap(() => this.api.get('/notifications/unread-count').pipe(
        catchError(() => of({ count: 0 }))
      ))
    ).subscribe((res: any) => this._unread$.next(res?.count || 0));
  }

  stop(): void {
    this.poll?.unsubscribe();
    this.poll = undefined;
  }

  refreshList(): void {
    this.api.get('/notifications').pipe(
      catchError(() => of([]))
    ).subscribe((list: any) => {
      const items = Array.isArray(list) ? list : [];
      this._items$.next(items);
      this._unread$.next(items.filter((n: AppNotification) => !n.read).length);
    });
  }

  markRead(n: AppNotification): void {
    if (n.read) return;
    n.read = true;
    this._items$.next(this._items$.value);
    this._unread$.next(Math.max(0, this._unread$.value - 1));
    this.api.patch('/notifications/' + n.id + '/read', {}).pipe(catchError(() => of(null))).subscribe();
  }

  markAllRead(): void {
    const items = this._items$.value.map(n => ({ ...n, read: true }));
    this._items$.next(items);
    this._unread$.next(0);
    this.api.post('/notifications/read-all', {}).pipe(catchError(() => of(null))).subscribe();
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
